import React from 'react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, ReferenceLine
} from 'recharts';

const navData = Array.from({ length: 24 }, (_, i) => {
  const month = new Date(2024, i, 1);
  const label = month?.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
  return { month: label, nav: +(85 + i * 3.2 + Math.sin(i) * 4)?.toFixed(1) };
});

const quarterlyData = [
  { quarter: 'Q1 24', distributions: 4.2, capitalCalls: -8.5 },
  { quarter: 'Q2 24', distributions: 5.1, capitalCalls: -6.2 },
  { quarter: 'Q3 24', distributions: 6.8, capitalCalls: -4.1 },
  { quarter: 'Q4 24', distributions: 7.4, capitalCalls: -3.8 },
  { quarter: 'Q1 25', distributions: 8.2, capitalCalls: -2.5 },
  { quarter: 'Q2 25', distributions: 9.1, capitalCalls: -1.8 },
  { quarter: 'Q3 25', distributions: 10.3, capitalCalls: -1.2 },
  { quarter: 'Q4 25', distributions: 11.6, capitalCalls: -0.8 },
];

const MetricCard = ({ label, value, sub }) => (
  <div className="card" style={{ padding: '16px 20px' }}>
    <p className="font-caption text-muted-foreground mb-1" style={{ fontSize: '0.7rem', letterSpacing: '0.05em', textTransform: 'uppercase' }}>{label}</p>
    <p className="font-heading font-semibold text-foreground" style={{ fontSize: '1.5rem', lineHeight: 1.2 }}>{value}</p>
    {sub && <p className="font-caption text-muted-foreground text-xs mt-1">{sub}</p>}
  </div>
);

export default function PerformanceTab({ asset }) {
  return (
    <div className="space-y-6">
      {/* Metric Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <MetricCard label="Commitment" value={`£${asset?.commitment}m`} sub="" />
        <MetricCard label="Capital Deployed" value={`£${asset?.deployed}m`} sub={`${((asset?.deployed / asset?.commitment) * 100)?.toFixed(0)}% of commitment`} />
        <MetricCard label="Net IRR" value={`${asset?.irr?.toFixed(1)}%`} sub="Since inception" />
        <MetricCard label="MOIC" value={`${asset?.moic?.toFixed(2)}x`} sub="" />
        <MetricCard label="DPI" value="0.84x" sub="Distributions / paid-in" />
        <MetricCard label="TVPI" value={`${(asset?.moic * 0.92)?.toFixed(2)}x`} sub="Total value / paid-in" />
      </div>
      {/* NAV Chart */}
      <div className="card" style={{ padding: '20px 24px' }}>
        <h3 className="font-heading font-semibold text-foreground mb-1" style={{ fontSize: '0.9375rem' }}>NAV Progression</h3>
        <p className="font-caption text-muted-foreground text-xs mb-4">Monthly net asset value — 24 months · Sample data</p>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={navData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="navGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={0.18} />
                <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} tickLine={false} axisLine={false} tickFormatter={v => `£${v}m`} />
            <Tooltip formatter={(v) => [`£${v}m`, 'NAV']} contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--color-border)' }} />
            <Area type="monotone" dataKey="nav" stroke="var(--color-primary)" strokeWidth={2} fill="url(#navGrad)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      {/* Quarterly Distributions vs Capital Calls */}
      <div className="card" style={{ padding: '20px 24px' }}>
        <h3 className="font-heading font-semibold text-foreground mb-1" style={{ fontSize: '0.9375rem' }}>Quarterly Distributions vs Capital Calls</h3>
        <p className="font-caption text-muted-foreground text-xs mb-4">8 quarters · £m · Sample data</p>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={quarterlyData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis dataKey="quarter" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} tickLine={false} axisLine={false} tickFormatter={v => `£${v}m`} />
            <Tooltip formatter={(v, n) => [`£${Math.abs(v)}m`, n === 'distributions' ? 'Distributions' : 'Capital Calls']} contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--color-border)' }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <ReferenceLine y={0} stroke="var(--color-border)" />
            <Bar dataKey="distributions" name="Distributions" fill="#059669" radius={[3, 3, 0, 0]} />
            <Bar dataKey="capitalCalls" name="Capital Calls" fill="#DC2626" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
